import { useState, useEffect, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { useDispatch } from 'react-redux'
import { Pressable } from '@react-aria/interactions'
import { HexColorPicker, HexColorInput } from 'react-colorful'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Text } from 'blaze-ui/Text/Text'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { getTextColorForBackground } from 'utils/colorHelpers'
import { useUpdateWorkspaceSettings } from 'hooks/queries/workspaces/useUpdateWorkspaceSettings'
import * as Actions from 'redux/actions'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { applyWhiteLabelColor } from 'utils/applyWhiteLabelColor'
import { Workspace } from 'types'
import Styles from './BrandColorPicker.module.scss'

const HEX_COLOR_REGEX = /^#?[0-9A-F]{6}$/i
const SAVE_DEBOUNCE_MS = 500

export const BrandColorPicker = observer(({ workspace }: { workspace: Workspace }) => {
  const [color, setColor] = useState(workspace.white_label_color || '#000000')
  const [tempColor, setTempColor] = useState(workspace.white_label_color || '#000000')
  const [initialColor, setInitialColor] = useState(workspace.white_label_color || '#000000')
  const [previewToggleChecked, setPreviewToggleChecked] = useState(true)
  const { mutateAsync } = useUpdateWorkspaceSettings(workspace.key)
  const dispatch = useDispatch()
  const workspaces = useWorkspaces()

  const saveColor = useCallback(
    async (newColor: string) => {
      if (!HEX_COLOR_REGEX.test(newColor)) return

      try {
        await mutateAsync({ white_label_color: newColor })
        dispatch(
          Actions.workspacesSet(
            workspaces.map((w) => (w.key === workspace.key ? { ...w, white_label_color: newColor } : w)),
          ),
        )
        applyWhiteLabelColor(newColor)
      } catch (error) {
        showToastMessage('Error saving color', 'error')
      }
    },
    [mutateAsync, dispatch, workspace.key, workspaces],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (color && color !== workspace.white_label_color) {
        saveColor(color)
      }
    }, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [color, workspace.white_label_color, saveColor])

  const updateColor = (newColor: string) => {
    const formatted = newColor.startsWith('#') ? newColor.toUpperCase() : `#${newColor}`.toUpperCase()
    setTempColor(formatted)
    setColor(formatted)
  }

  const resetColor = () => {
    setTempColor(initialColor)
    setColor(initialColor)
  }

  const handlePickerOpen = () => {
    setInitialColor(color)
    setTempColor(color)
  }

  const previewColor = tempColor || color
  const textColor = getTextColorForBackground(previewColor)

  return (
    <div className={Styles.colorSection}>
      <div className={Styles.preview}>
        <div className={Styles.previewHeader}>
          <Text variant="secondary" color="var(--dark-40)">
            Here's a preview of your toggle and button colors:
          </Text>
        </div>
        <div className={Styles.previewContent}>
          <div className={Styles.toggleRow}>
            <div
              className={Styles.previewToggle}
              onClick={() => setPreviewToggleChecked(!previewToggleChecked)}
              style={
                {
                  '--toggle-bg': previewToggleChecked ? previewColor : 'var(--dark-10)',
                } as React.CSSProperties
              }
            >
              <div className={Styles.toggleKnob} data-checked={previewToggleChecked} />
            </div>
            <span className={Styles.toggleLabel}>Toggle Color</span>
          </div>
          <div
            className={Styles.previewButton}
            style={
              {
                '--preview-bg': previewColor,
                '--preview-text': textColor,
              } as React.CSSProperties
            }
          >
            Button Color
          </div>
        </div>
      </div>

      <div className={Styles.colorInputContainer}>
        <DropdownTrigger
          popoverClassName={Styles.colorPickerPopover}
          placement="bottom-start"
          onOpenChange={(isOpen) => {
            if (isOpen) {
              handlePickerOpen()
            }
          }}
        >
          <Pressable>
            <div className={Styles.inputField}>
              <div className={Styles.colorCircle} style={{ backgroundColor: color }} />
              <span className={Styles.hash}>#</span>
              <span className={Styles.hexValue}>{color.replace('#', '')}</span>
            </div>
          </Pressable>
          {() => (
            <div className={Styles.pickerWrapper}>
              <div className={Styles.pickerHeader}>
                <div className={Styles.hexInputInPicker}>
                  <div className={Styles.colorCircleInPicker} style={{ backgroundColor: tempColor }} />
                  <div className={Styles.hexInputWrapper}>
                    <span className={Styles.hashInPicker}>#</span>
                    <HexColorInput
                      className={Styles.hexInput}
                      color={tempColor}
                      onChange={updateColor}
                      aria-label="Hex color value"
                    />
                  </div>
                </div>
                <button className={Styles.resetButton} onClick={resetColor} type="button">
                  Reset
                </button>
              </div>
              <HexColorPicker color={tempColor} onChange={updateColor} />
            </div>
          )}
        </DropdownTrigger>
      </div>
    </div>
  )
})
